import { useEffect, useState, useSyncExternalStore } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { RefreshCw, WifiOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { updateStore } from '@/pwa/update-store'
import { applyUpdate } from '@/pwa/register'
import { isSafeToRefresh } from '@/pwa/transaction-guard'

/**
 * Update notification: a new version was downloaded in the background and is
 * waiting. Nothing reloads until the user presses "Update now", and even then
 * the transaction guard may defer it (see `isSafeToRefresh`).
 */
export function UpdateNotification() {
  const queryClient = useQueryClient()
  const state = useSyncExternalStore(updateStore.subscribe, updateStore.getSnapshot)
  const [dismissedForSession, setDismissedForSession] = useState(false)

  // While the refresh was refused, watch the transaction guard and lift the
  // "busy" hint by itself as soon as the sale/payment/form is finished — the
  // user never has to guess whether it is safe to press again.
  const [busy, setBusy] = useState(state.deferredByTransaction)
  useEffect(() => {
    if (!state.deferredByTransaction) {
      setBusy(false)
      return
    }
    setBusy(true)
    const timer = window.setInterval(() => {
      if (isSafeToRefresh(queryClient)) {
        updateStore.set({ deferredByTransaction: false })
        setBusy(false)
      }
    }, 2000)
    return () => window.clearInterval(timer)
  }, [state.deferredByTransaction, queryClient])

  if (state.offlineReady && !state.updateAvailable) {
    return (
      <div
        className="fixed bottom-3 left-3 z-[60] flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 shadow-lg"
        role="status"
      >
        <WifiOff className="h-4 w-4 text-emerald-600" aria-hidden="true" />
        StockWise is ready to work offline.
      </div>
    )
  }

  if (!state.updateAvailable || dismissedForSession) return null

  const onUpdate = () => {
    void applyUpdate(queryClient).then((applied) => {
      if (!applied) setBusy(true)
    })
  }

  return (
    <aside
      className="fixed z-[60] left-3 bottom-3 sm:left-4 sm:bottom-4 w-[min(22rem,calc(100vw-1.5rem))] rounded-xl border border-slate-200 bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-lg shadow-slate-900/10"
      role="region"
      aria-label="StockWise update available"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
          <RefreshCw className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <h2 className="text-sm font-semibold text-slate-900">StockWise update available</h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">
            A new version of StockWise is ready with improvements and fixes.
          </p>
          {busy && (
            <p className="mt-2 rounded-md bg-amber-50 px-2 py-1.5 text-xs leading-relaxed text-amber-800" role="alert">
              A sale, payment or unsaved form is in progress — the update will wait until it
              finishes.
            </p>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setDismissedForSession(true)
            updateStore.set({ deferredByTransaction: false })
          }}
        >
          Later
        </Button>
        <Button size="sm" onClick={onUpdate} disabled={state.applying}>
          {state.applying ? 'Updating…' : 'Update now'}
        </Button>
      </div>
    </aside>
  )
}

export default UpdateNotification
